import React, { useContext, useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import * as MenubarPrimitive from '@radix-ui/react-menubar';
import styled, { css } from 'styled-components';
import { includes, findIndex } from 'lodash-es';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ref, onValue } from 'firebase/database';
import { useLocation } from 'react-router-dom';

import { AuthContext } from '@app/providers/firebase/AuthProvider';

import { showMessageCenterAtom, showSettingsCenterAtom } from '@shared/state/messageState';
import { usePermissions } from '@shared/permissions/usePermissions';
import { menubarVisibleAtom, MENUBAR_HEIGHT } from '@shared/state/navState';
import { clearRedirect } from '@lib/util';
import { useTestDataRemote } from '@lib/util/site';
import { clearCache as clearLocalCache } from '@services/db/localCache';
import ScopedComponent from '@ui/Utility/ScopedComponent';
import { IMessage } from '@shared/types/dbRecords';
import useFirebase from '@app/providers/firebase';
import { signOut } from 'firebase/auth';
import db from '@services/db';
import theme from '@config/theme';
import { FlexRow } from '@ui/Containers/FlexContainer';
import { rgba } from '@config/palette/utils';
import Modal from '@ui/Display/Modal';
import { throttle, rafThrottle, debounce } from '@shared/utils/throttle';

// Import from the new menuItems.ts
import { getMenuItems, MenuCategory, MenuItem, ActionMenuItem } from './menuItems';

/** Staging nav chrome — matches App.tsx test-data tinting */
const STAGING_RED = '#FF0000';
const STAGING_RED_DARK = '#7a211b';
const STAGING_RED_HOVER = '#c72d22';
const STAGING_RED_SOFT = 'rgba(255, 0, 0, 0.075)';
const STAGING_RED_ALT_ROW = 'rgba(255, 0, 0, 0.08)';

const ComponentWrapper = styled.div<{ isVisible: boolean }>`
  width: 100%;
  height: ${MENUBAR_HEIGHT}px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: none;
  z-index: 99998;
  background-color: ${rgba(theme.palette.neutral.white, 0.95)};
  backdrop-filter: blur(8px);
  box-shadow: ${({ isVisible }) => isVisible 
    ? 'rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px' 
    : 'none'};
  transform: translateY(${({ isVisible }) => isVisible ? '0' : '-100%'});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease;
  
  @media ${theme.device.laptopL} {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const SiteLogo = styled(FlexRow)<{ logoTop: string; logoLeft: string; isVisible: boolean; $staging?: boolean }>`
  width: 130px;
  height: 48px;
  position: fixed;
  top: ${(props) => props.logoTop};
  left: 0px;
  // Hide with the menubar via transform
  transform: translateY(${({ isVisible }) => isVisible ? '0' : '-100%'});
  transition: top 0.3s cubic-bezier(0.4, 0, 0.2, 1), left 0.45s ease-in-out, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 999999;
  
  & p {
    cursor: default;
    color: ${({ $staging }) => ($staging ? STAGING_RED : theme.palette.primary.hue)};
    font-size: 18px;
    letter-spacing: 14px;
    font-weight: 400;
    margin: 0;
    transition: letter-spacing 0.6s ease-in-out, font-weight 0.6s ease-in-out, text-shadow 0.6s ease-in-out;
    &:hover {
      letter-spacing: 15px;
      font-weight: 500;
      text-shadow: ${rgba(theme.palette.primary.hue, 0.1)} 0px 2px 8px 0px;
    }
  }

  @media ${theme.device.laptop} {
    left: ${(props) => props.logoLeft}; 
  }
`;
  
const MenuContentContainer = styled(FlexRow)` 
  width: 80%;
  height: 48px;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  margin: 0 auto;
  padding-left: 140px; // Space for the SiteLogo
`;

const MenubarRootWrapper = styled(FlexRow)` // Renamed from ComponentWrapper for clarity
  width: 100%; // Takes full width of MenubarContainer's content box
  height: 48px;
  align-items: center;
  justify-content: flex-start; // Menu items start after logo
  gap: 4px;
  // margin: 4px auto 0 auto; // Centering handled by MenubarContainer
  padding-left: 140px; // Space for the SiteLogo (width + gap)

  // This wrapper needs to start AFTER the SiteLogo

  @media ${theme.device.desktop} { // Adjust breakpoint as needed
    // Desktop specific adjustments if any, but padding-left should mostly handle it
  }
`;

const MenubarRoot = styled(MenubarPrimitive.Root)`
  width: 100%; // Takes full width of MenubarRootWrapper's content box
  height: 48px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

const MenubarTrigger = styled(MenubarPrimitive.Trigger)<{ isActive?: boolean; $staging?: boolean }>`
  // min-width: calc(12.5% - 20.5px); // (100% / 8 triggers) - (logo width / 8 triggers)
  flex-grow: 2;
  height: 40px;
  padding: 0 10px;
  background-color: ${theme.palette.neutral.white};
  // border: 1px solid ${theme.palette.neutral[200]};
  // border-right: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-align: center;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px;
  font-size: 16px;
  color: ${({ $staging }) => ($staging ? STAGING_RED_DARK : 'inherit')};
  
  ${({ isActive, $staging }) =>
    isActive &&
  css`
    background-color: ${$staging ? STAGING_RED_SOFT : theme.palette.primary[100]};
    color: ${$staging ? STAGING_RED : theme.palette.primary.hue};
    font-weight: 600;
    box-shadow: 0px 3px 2px 0px ${rgba($staging ? STAGING_RED : theme.palette.primary.D100, .3)}, 0px 5px 3px -2px inset ${rgba($staging ? STAGING_RED_DARK : theme.palette.primary.D500, 0.2)} inset; 
  `}
  
  &:hover,
  &[data-state='open'] {
    background-color: ${({ $staging }) => ($staging ? STAGING_RED : theme.palette.primary.hue)};
    color: ${theme.palette.neutral.white};
    box-shadow: 0px 3px 2px 0px ${rgba(theme.palette.primary.D100, .3)}, 0px 5px 3px -2px inset ${rgba(theme.palette.primary.D500, 0.2)} inset; 
  }

  &:focus-visible {
    outline: none;
    border: none;
  }

  @media ${theme.device.desktop} {
    font-size: 14px;
  }
`;

const MenubarContent = styled(MenubarPrimitive.Content)`
  width: var(--radix-menubar-trigger-width); // Dynamically set width to match trigger
  z-index: 99990;
  background-color: white;
  padding: 5px;
  box-shadow: 0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2);

  &:focus-visible {
    outline: none;
    border: none;
  }
`;

const MenubarSeparator = styled(MenubarPrimitive.Separator)<{ $staging?: boolean }>`
  height: 1px;
  width: 80%;
  margin: 8px auto;
  background-color: ${({ $staging }) => ($staging ? 'rgba(255, 0, 0, 0.25)' : theme.palette.primary[300])};
  border: none;
`;

const MenubarItemStyled = styled(MenubarPrimitive.Item)<{ isAlternate: boolean; $staging?: boolean }>`
  width: 100%;
  margin: 0 auto;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0s linear;
  border: none;
  background-color: ${({ isAlternate, $staging }) => {
    if (!isAlternate) return 'transparent';
    return $staging ? STAGING_RED_ALT_ROW : rgba(theme.palette.primary[300], 0.2);
  }};
  box-shadow: rgba(0, 0, 0, 0.05) 0px 1px 2px 0px;

  &:hover,
  &:focus,
  &:active {
    background-color: ${({ $staging }) => ($staging ? STAGING_RED_HOVER : theme.palette.primary[700])};
    color: ${theme.palette.neutral.white};
    box-shadow: rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px;
    border: 0px solid ${({ $staging }) => ($staging ? STAGING_RED_HOVER : theme.palette.primary[700])};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

// BaseMenuItem, ActionMenuItem, SeparatorMenuItem, MenuItem types are now imported from ./menuItems

const DesktopMenubar: React.FC = () => {
  const { currentUser } = useContext(AuthContext);
  const stagingNav = useTestDataRemote;
  const { pathname } = useLocation();
  const [_mailCount, setMailCount] = useState<number>(0);
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(true);
  
  const initialLogoTopConst = '4px';
  const initialLogoLeftConst = '10.5vw';
  
  const [logoTop] = useState(initialLogoTopConst);
  const [logoLeft] = useState(initialLogoLeftConst);
  const setShowMessageCenter = useSetRecoilState(showMessageCenterAtom);
  const setShowSettingsCenter = useSetRecoilState(showSettingsCenterAtom);
  const setMenubarVisible = useSetRecoilState(menubarVisibleAtom);
  const history = useHistory();
  const { auth } = useFirebase();

  const { hasPermission } = usePermissions();
  const isInternalUser = hasPermission(['orders.view', 'schedule.view', 'router.scan']);

  // Refs for scroll handling
  const prevScrollYRef = useRef<number>(0);
  const scrollDirectionRef = useRef<'up' | 'down' | null>(null);
  
  // Ref for menu close timeout (prevents menu closing during cursor travel to submenu)
  const menuCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Scroll-direction-aware visibility handling
  useEffect(() => {
    prevScrollYRef.current = window.scrollY;
    
    // Minimum scroll distance to trigger hide/show (prevents jitter)
    const SCROLL_THRESHOLD = 10;
    // Always show menubar when near the top of the page
    const TOP_THRESHOLD = 100;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDelta = currentScrollY - prevScrollYRef.current;
      
      // Always show when near the top of the page
      if (currentScrollY < TOP_THRESHOLD) {
        if (!isVisible) {
          setIsVisible(true);
          setMenubarVisible(true);
        }
        prevScrollYRef.current = currentScrollY;
        return;
      }
      
      // Only trigger if scroll distance exceeds threshold (prevents micro-movements)
      if (Math.abs(scrollDelta) < SCROLL_THRESHOLD) {
        return;
      }
      
      const newDirection = scrollDelta > 0 ? 'down' : 'up';
      
      // Only update if direction changed or visibility needs updating
      if (newDirection !== scrollDirectionRef.current) {
        scrollDirectionRef.current = newDirection;
        
        if (newDirection === 'down' && isVisible) {
          // Scrolling down: hide the menubar
          setIsVisible(false);
          setMenubarVisible(false);
        } else if (newDirection === 'up' && !isVisible) {
          // Scrolling up: show the menubar
          setIsVisible(true);
          setMenubarVisible(true);
        }
      }
      
      prevScrollYRef.current = currentScrollY;
    };

    // Use throttle for scroll performance
    const throttledHandleScroll = throttle(handleScroll, 50);

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [isVisible, setMenubarVisible]); 

  useEffect(() => {
    if (currentUser && currentUser.email && db.database) {
      const mailDbString = '/mail';
      const mailRef = ref(db.database, mailDbString);
      const unsubscribe = onValue(mailRef, (snapshot) => {
        const data = snapshot.val() || {};
        const unreadMessages = Object.values(data.unread || {}) as IMessage[];
        const personalUnread = Object.values(data[currentUser.email.split('@')[0]]?.unread || {}) as IMessage[];
        const communalUnread = unreadMessages.filter((m: IMessage) => includes(m.users, currentUser.email.split('@')[0]));
        const combinedMail = [...personalUnread, ...communalUnread];
        setMailCount(combinedMail.length);
      });
      return () => unsubscribe();
    }
  }, [currentUser]);

  // Cleanup menu close timeout on unmount
  useEffect(() => {
    return () => {
      if (menuCloseTimeoutRef.current) {
        clearTimeout(menuCloseTimeoutRef.current);
      }
    };
  }, []);

  const handleNavigation = (url: string) => {
    clearRedirect();
    history.push(url);
  };

  const onLogout = () => {
    Modal.confirm({
      content: 'Log out?',
      okText: 'Log out',
      cancelText: 'Cancel',
      onOk: () => {
        signOut(auth).then(() => {
          history.push('/login');
        });
      },
    });
  };

  const toggleMailDrawer = () => {
    setShowMessageCenter(true);
  };

  const toggleSettingsCenter = () => {
    setShowSettingsCenter(true);
  };

  const handleClearCache = () => {
    Modal.confirm({
      title: 'Clear Cache',
      content: 'This will clear the local cache and refresh the page. Continue?',
      okText: 'Clear Cache',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await clearLocalCache();
          window.location.reload();
        } catch (error) {
          Modal.error({ title: 'Error', content: 'Failed to clear cache.'});
        }
      },
    });
  };

  // Function to handle mouse enter on menu trigger
  const handleMenuMouseEnter = (menuTrigger: string) => {
    // Cancel any pending close timeout
    if (menuCloseTimeoutRef.current) {
      clearTimeout(menuCloseTimeoutRef.current);
      menuCloseTimeoutRef.current = null;
    }
    setHoveredMenu(menuTrigger);
  };

  // Function to handle mouse leave on menu trigger
  const handleMenuMouseLeave = () => {
    // Delay closing to allow cursor to travel to submenu content
    menuCloseTimeoutRef.current = setTimeout(() => {
      setHoveredMenu(null);
    }, 150); // 150ms grace period
  };

  // Function to handle mouse enter on menu content
  const handleContentMouseEnter = (menuTrigger: string) => {
    // Cancel any pending close timeout - cursor arrived at submenu
    if (menuCloseTimeoutRef.current) {
      clearTimeout(menuCloseTimeoutRef.current);
      menuCloseTimeoutRef.current = null;
    }
    setHoveredMenu(menuTrigger);
  };

  // Function to handle mouse leave on menu content
  const handleContentMouseLeave = () => {
    // Delay closing to allow cursor to potentially return
    menuCloseTimeoutRef.current = setTimeout(() => {
      setHoveredMenu(null);
    }, 150); // 150ms grace period
  };

  // Function to handle menu item selection
  const handleMenuItemSelect = (action: () => void) => {
    // Clear any pending close timeout
    if (menuCloseTimeoutRef.current) {
      clearTimeout(menuCloseTimeoutRef.current);
      menuCloseTimeoutRef.current = null;
    }
    // Close menu after selection
    setHoveredMenu(null);
    // Execute the action
    action();
  };

  if (!currentUser) {
    return null;
  }

  const allMenus: MenuCategory[] = getMenuItems({
    handleNavigation,
    toggleMailDrawer,
    toggleSettingsCenter,
    onLogout,
    handleClearCache,
  });

  if (!isInternalUser) return null;

  return (
    <ComponentWrapper isVisible={isVisible}>
      <SiteLogo logoTop={logoTop} logoLeft={logoLeft} isVisible={isVisible} $staging={stagingNav}>
        <p>HELM</p>
      </SiteLogo>
        <MenuContentContainer>
          <MenubarRoot 
            className="MenubarRoot"
            value={hoveredMenu || undefined} // Pass hoveredMenu as value, ensure it's string or undefined
            onValueChange={setHoveredMenu} // Allow Radix to update hoveredMenu state
          >
          {allMenus.map((menu) => {
            const hasVisibleItem = menu.items.some(item => {
              if (item.whitelist && item.whitelist.length > 0) {
                return includes(item.whitelist, currentUser.email);
              }
              return true;
            });

            if (!hasVisibleItem) {
              return null;
            }
            
            const isActive = menu.activePathPrefixes.some(prefix => pathname.startsWith(prefix));
            
            return (
              // @ts-ignore
              <MenubarPrimitive.Menu 
                key={menu.trigger}
                value={menu.trigger} // Use menu.trigger as the value for this menu
              >
                <MenubarTrigger 
                  className="MenubarTrigger" 
                  isActive={isActive}
                  $staging={stagingNav}
                  onMouseEnter={() => handleMenuMouseEnter(menu.trigger)}
                  onMouseLeave={handleMenuMouseLeave}
                >
                  {menu.trigger}
                </MenubarTrigger>
                <MenubarPrimitive.Portal>
                  <MenubarContent
                    className="MenubarContent" 
                    align="start"
                    sideOffset={8}
                    onMouseEnter={() => handleContentMouseEnter(menu.trigger)}
                    onMouseLeave={handleContentMouseLeave}
                  >
                    {(() => {
                      return menu.items.map((item, index) => (
                        <ScopedComponent 
                          key={`${menu.trigger}-${item.type === 'separator' ? 'separator' : (item as ActionMenuItem).label}-${index}`} 
                          whitelist={item.whitelist || []}
                        >
                          {item.type === 'separator' ? (
                            <MenubarSeparator className="MenubarSeparator" $staging={stagingNav} />
                          ) : (
                            (() => {
                              const isAlternate = findIndex(menu.items.filter((i) => i.type === 'action'), (i) => i.label === item.label) % 2 === 1; // Determine if this should be alternate style
                              
                              return (
                                <MenubarItemStyled
                                  className="MenubarItem"
                                  isAlternate={isAlternate}
                                  $staging={stagingNav}
                                  onSelect={() => handleMenuItemSelect((item as ActionMenuItem).action)}
                                >
                                  {(item as ActionMenuItem).label}
                                </MenubarItemStyled>
                              );
                            })()
                          )}
                        </ScopedComponent>
                      ));
                    })()}
                  </MenubarContent>
                </MenubarPrimitive.Portal>
              </MenubarPrimitive.Menu>
            );
          })}
          </MenubarRoot>
        </MenuContentContainer>
    </ComponentWrapper>
  );
};

export default DesktopMenubar;